import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import ScrollToTop from '../components/ScrollToTop';
import { useSEO } from '../hooks/useSEO';
import { TRIP_TEMPLATES, TEMPLATE_CATEGORIES, TripTemplate } from '../data/tripTemplates';
import { MapPin, Clock, Calendar, ArrowRight, Sparkles } from 'lucide-react';

const ExplorePage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [hoveredTrip, setHoveredTrip] = useState<string | null>(null);

  useSEO({
    title: 'Explore Curated Itineraries — Aura Traveler',
    description: 'Browse AI-crafted luxury travel itineraries for the world\'s most inspiring destinations. Paris, Bali, Santorini, Tokyo, and more — each one ready to customize.',
    canonical: 'https://www.auratraveler.com/explore',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'Explore', url: 'https://www.auratraveler.com/explore' },
    ],
  });

  const filtered = useMemo(() => {
    if (activeCategory === 'all') return TRIP_TEMPLATES;
    return TRIP_TEMPLATES.filter(t => t.category === activeCategory);
  }, [activeCategory]);

  const featured = useMemo(() => TRIP_TEMPLATES.filter(t => t.featured).slice(0, 3), []);

  return (
    <div className="min-h-screen bg-charcoal text-ivory">
      <ScrollToTop />
      <SiteNav />

      {/* Hero */}
      <div className="relative pt-28 pb-16 px-6 sm:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #1a1510 40%, #0f1a1a 70%, #0A0A0A 100%)' }}>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(201,169,110,0.4) 0%, transparent 50%), radial-gradient(circle at 70% 30%, rgba(100,140,180,0.3) 0%, transparent 50%)' }} />
        <div className="max-w-5xl mx-auto relative text-center">
          <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold mb-5">✦ Curated Expeditions</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-ivory leading-[1.05] mb-5" style={{ fontWeight: 300 }}>
            Explore the <em className="font-serif text-gold">world's finest</em> itineraries
          </h1>
          <p className="font-sans text-sm text-ivory/50 max-w-xl mx-auto" style={{ lineHeight: 1.8 }}>
            Each itinerary is AI-crafted with real venues, insider tips, and luxury recommendations. Pick one and make it yours.
          </p>
        </div>
      </div>

      {/* Featured Trips */}
      {featured.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 sm:px-8 py-14">
          <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold/50 mb-6">Featured</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featured.map((trip, i) => (
              <FeaturedCard key={trip.id} trip={trip} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* Category Filter */}
      <div className="max-w-6xl mx-auto px-6 sm:px-8 pt-8 pb-4">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {TEMPLATE_CATEGORIES.map(cat => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-5 py-2.5 rounded-full text-xs font-sans uppercase tracking-widest border transition-all shrink-0 ${
                activeCategory === cat.key
                  ? 'bg-gold text-charcoal border-gold font-bold'
                  : 'bg-transparent text-ivory/40 border-ivory/10 hover:border-gold/30 hover:text-ivory'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Trip Grid */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-8 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((trip, i) => (
            <TripCard
              key={trip.id}
              trip={trip}
              index={i}
              isHovered={hoveredTrip === trip.id}
              onHover={() => setHoveredTrip(trip.id)}
              onLeave={() => setHoveredTrip(null)}
            />
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-16">
            <p className="font-serif italic text-ivory/40">No itineraries in this category yet.</p>
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="py-16 px-6 sm:px-8 bg-charcoal border-t border-ivory/5">
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-display text-3xl text-ivory mb-3" style={{ fontWeight: 300, fontStyle: 'italic' }}>
            Don't see your destination?
          </p>
          <p className="font-sans text-sm text-ivory/40 mb-8">
            Aura Traveler generates bespoke itineraries for any destination on earth — in under 90 seconds.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/" className="inline-flex items-center gap-2 bg-gold text-charcoal px-8 py-4 font-sans text-[11px] uppercase tracking-[0.12em] font-bold rounded-xl hover:bg-gold/90 shadow-lg shadow-gold/10">
              <Sparkles className="w-4 h-4" /> Design Your Own <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/day-planner" className="inline-flex items-center gap-2 px-6 py-4 border border-ivory/15 text-ivory/50 rounded-xl font-sans text-[11px] uppercase tracking-[0.1em] hover:border-gold/30 hover:text-ivory transition-all">
              Try Free Day Planner
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

// ── Featured Card (large, horizontal) ───────────────────────────────────────
const FeaturedCard: React.FC<{ trip: TripTemplate; index: number }> = ({ trip, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1, duration: 0.5 }}
  >
    <Link to={`/destinations/${trip.id}`} className="group block">
      <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
        <img
          src={trip.heroImage}
          alt={`${trip.name} — ${trip.destination}`}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          loading={index === 0 ? 'eager' : 'lazy'}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span className="inline-block px-3 py-1 rounded-full text-[10px] font-sans uppercase tracking-widest text-gold bg-black/40 backdrop-blur-sm mb-3">{trip.category}</span>
          <h3 className="font-display text-2xl text-white mb-1" style={{ fontWeight: 400 }}>{trip.name}</h3>
          <div className="flex items-center gap-3 text-white/60 text-xs font-sans">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{trip.destination}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{trip.duration}</span>
          </div>
        </div>
      </div>
    </Link>
  </motion.div>
);

// ── Trip Card (grid item) ───────────────────────────────────────────────────
const TripCard: React.FC<{ trip: TripTemplate; index: number; isHovered: boolean; onHover: () => void; onLeave: () => void }> = ({ trip, index, isHovered, onHover, onLeave }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05, duration: 0.4 }}
    onMouseEnter={onHover}
    onMouseLeave={onLeave}
  >
    <Link to={`/destinations/${trip.id}`} className="group block">
      <div className="rounded-xl overflow-hidden border border-ivory/[0.06] hover:border-gold/20 transition-all duration-500">
        {/* Image */}
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={trip.heroImage}
            alt={`${trip.name} — ${trip.destination}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-transparent opacity-60" />
          <div className="absolute top-3 left-3">
            <span className="px-2.5 py-1 rounded-full text-[10px] font-sans uppercase tracking-widest text-gold bg-charcoal/70 backdrop-blur-sm">{trip.category}</span>
          </div>
          <div className="absolute bottom-3 right-3">
            <span className="px-2.5 py-1 rounded-full text-[10px] font-sans uppercase tracking-widest text-ivory/60 bg-charcoal/70 backdrop-blur-sm">{trip.priceRange}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 bg-charcoal/40">
          <h3 className="font-display text-lg text-ivory mb-1 group-hover:text-gold transition-colors" style={{ fontWeight: 400 }}>{trip.name}</h3>
          <div className="flex items-center gap-3 mb-3">
            <span className="flex items-center gap-1 text-[10px] font-sans text-ivory/40"><MapPin className="w-3 h-3" />{trip.destination}</span>
            <span className="flex items-center gap-1 text-[10px] font-sans text-ivory/40"><Clock className="w-3 h-3" />{trip.duration}</span>
            <span className="flex items-center gap-1 text-[10px] font-sans text-ivory/40"><Calendar className="w-3 h-3" />{trip.bestSeason}</span>
          </div>
          <p className="font-serif text-xs text-ivory/40 italic line-clamp-2 mb-4">{trip.description}</p>

          {/* Highlights */}
          <div className="flex flex-wrap gap-1.5">
            {trip.highlights.slice(0, 3).map((h, i) => (
              <span key={i} className="px-2 py-0.5 rounded text-[10px] font-sans text-ivory/30 bg-ivory/[0.04]">{h}</span>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-4 pt-4 border-t border-ivory/[0.04] flex items-center justify-between">
            <span className="font-sans text-[10px] uppercase tracking-widest text-gold/50">View Itinerary</span>
            <ArrowRight className="w-4 h-4 text-gold/30 group-hover:text-gold group-hover:translate-x-1 transition-all" />
          </div>
        </div>
      </div>
    </Link>
  </motion.div>
);

export default ExplorePage;
